// Where to send the browser after a Heavybit Identity login, and how to tell
// that the login started from a Connected App consent flow.
//
// `return_to` arrives on the query string from whoever sent the person to
// /identity/login (the consent route bouncing an unauthenticated visitor, or
// Crucible's signup link). It is an open-redirect vector, so only same-origin
// paths and https URLs on allowlisted origins (config.identity.returnOrigins)
// survive; anything else falls back to the done page.

import { validClientId as sharedValidClientId } from "@auger/identity"

export const DEFAULT_RETURN_TO = "/identity/done"
export const AUTHORIZE_PATH = "/identity/authorize"

/**
 * Normalize the VITE_IDENTITY_RETURN_ORIGINS entries into the exact strings
 * `URL.origin` produces, because that is what safeReturnTo compares them to.
 * A trailing slash or a capital in the host would otherwise match nothing, and
 * the only symptom is a person quietly landing on /identity/done instead of
 * the app that sent them. Anything that is not an https origin is dropped.
 */
export function normalizeReturnOrigins(raw: string | undefined): string[] {
  return (raw ?? "")
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean)
    .flatMap((entry) => {
      try {
        const url = new URL(entry)
        return url.protocol === "https:" ? [url.origin] : []
      } catch {
        return []
      }
    })
}

export function safeReturnTo(raw: unknown, allowedOrigins: readonly string[] = []): string {
  if (typeof raw !== "string") return DEFAULT_RETURN_TO
  // An absolute https URL is followed only when its origin is allowlisted
  // (another app on the Heavybit Identity project asking for its person back).
  if (/^https:\/\//i.test(raw)) {
    try {
      const url = new URL(raw)
      return url.protocol === "https:" && allowedOrigins.includes(url.origin)
        ? raw
        : DEFAULT_RETURN_TO
    } catch {
      return DEFAULT_RETURN_TO
    }
  }
  // Otherwise a relative path only: one leading slash, then not another slash
  // or a backslash (browsers treat "/\evil.com" like "//evil.com").
  if (!/^\/(?![/\\])/.test(raw)) return DEFAULT_RETURN_TO
  // No control characters (code points below 0x20, or 0x7f).
  for (const ch of raw) {
    const code = ch.charCodeAt(0)
    if (code < 0x20 || code === 0x7f) return DEFAULT_RETURN_TO
  }
  // Nothing in the path segment that could be parsed as a scheme.
  if (/^\/[^?#]*:/.test(raw.split(/[?#]/)[0] ?? "")) return DEFAULT_RETURN_TO
  return raw
}

/**
 * The origin to report to the exchange for first-touch attribution
 * (`users.signup_source`), or undefined when there is nothing honest to say.
 *
 * Takes the RAW stored return_to, not the validated one, because the two facts
 * the exchange needs to tell apart are:
 *
 * - **nothing was stored** — nobody sent this person here; they found the
 *   sign-in page. The exchange records that as `direct`, and it is the most
 *   interesting bucket there is (an uninvited walk-up, which is what the
 *   waitlist exists to catch).
 * - **something was stored and survived `safeReturnTo`** — an app sent them.
 *   Its origin goes to the exchange, which owns the origin → label mapping.
 *
 * `safeReturnTo` collapses those two: it answers `/identity/done` for "nothing
 * stored" and for "stored but refused" alike. So a stored value that does NOT
 * survive validation is undefined here too — a rejected origin is not evidence
 * of anything.
 *
 * A LABEL IS NEVER SENT. The caller can only name an origin, and only origins
 * on `config.returnOrigins` (plus this app's own) mean anything to the
 * exchange, so nobody can stamp a category of their own invention.
 */
export function attributionOrigin(
  stored: unknown,
  allowedOrigins: readonly string[] = [],
  selfOrigin = ""
): string | undefined {
  if (typeof stored !== "string" || stored === "") return undefined
  const validated = safeReturnTo(stored, allowedOrigins)
  // Refused: safeReturnTo bailed to the done page for something that was not
  // already the done page.
  if (validated === DEFAULT_RETURN_TO && stored !== DEFAULT_RETURN_TO) return undefined
  if (/^https:\/\//i.test(validated)) {
    try {
      return new URL(validated).origin
    } catch {
      return undefined
    }
  }
  // A same-origin path: another page of this app sent them here.
  return selfOrigin || undefined
}

/**
 * What happened to the stored return_to, for telemetry. safeReturnTo cannot
 * answer this: it returns the done page for a REFUSED target and for an
 * ABSENT one alike, which makes the two look identical everywhere downstream.
 * They are different failures. Absent means the person started at the
 * identity front door directly, so there was nowhere to send them back to.
 * Refused means somebody sent them here and the allowlist turned the target
 * away — usually a misconfigured VITE_IDENTITY_RETURN_ORIGINS, whose only
 * other symptom is a person quietly landing on the wrong page.
 *
 * Reports an ORIGIN at most, never a path or query string: a return_to can
 * carry single-use Stytch credentials, which is the whole reason analytics on
 * /identity/* is sanitised.
 */
export type ReturnToOutcome =
  | { outcome: "absent" }
  | { outcome: "same_origin" }
  | { outcome: "honoured"; origin: string }
  | { outcome: "refused"; origin: string | null }

export function returnToOutcome(
  stored: unknown,
  allowedOrigins: readonly string[] = []
): ReturnToOutcome {
  if (typeof stored !== "string" || stored === "") return { outcome: "absent" }
  const validated = safeReturnTo(stored, allowedOrigins)
  if (validated === DEFAULT_RETURN_TO && stored !== DEFAULT_RETURN_TO) {
    let origin: string | null
    try {
      origin = /^https?:\/\//i.test(stored) ? new URL(stored).origin : null
    } catch {
      origin = null
    }
    return { outcome: "refused", origin }
  }
  if (/^https:\/\//i.test(validated)) {
    return { outcome: "honoured", origin: new URL(validated).origin }
  }
  return { outcome: "same_origin" }
}

/**
 * The origin_assertion query parameter, read as-is with NO validation or
 * interpretation — it is opaque in the browser; only the exchange
 * (verifyOriginAssertion in functions/common/stytch.js) holds the shared
 * secret needed to check it. This function exists only to do the same
 * string-vs-array coercion every other query-param reader here does (vue-
 * router hands back a string[] when a parameter repeats), not to judge the
 * value.
 */
export function originAssertionFrom(raw: unknown): string | undefined {
  const value = Array.isArray(raw) ? raw[0] : raw
  return typeof value === "string" && value.length > 0 ? value : undefined
}

// The consent route is entered as /identity/authorize?client_id=…&scope=…
// (Stytch sends the OAuth authorization request there). When a login is only
// happening so that consent can proceed, the client id tells the exchange
// that this sign-in began at an agent surface; a plain browser login has no
// client_id. The exchange uses it for users.signup_source and its own log
// line, and for nothing else: it deliberately never reaches the session's
// Hasura claims, because the session is the person's own browser credential
// and a claim set there would outlive the consent flow by the session's whole
// lifetime. Agent provenance comes from the Connected App access token (see
// hasuraClaimsFrom in functions/common/stytch.js).
export function agentClientIdFrom(returnTo: string): string | undefined {
  // Same-origin only, and that has to be checked HERE rather than assumed from
  // the caller. `startsWith("/")` is not same-origin: new URL() parses the host
  // out of a protocol-relative value, so "//evil.com/identity/authorize" and
  // "/\evil.com/identity/authorize" both left pathname === AUTHORIZE_PATH and
  // returned an attacker-chosen id. The one production caller happens to pass a
  // post-safeReturnTo value, so it was never reachable — but the safety lived in
  // the caller while the comment here claimed it lived in the function, which is
  // how the next caller gets it wrong. Same test as safeReturnTo: one leading
  // slash, then neither another slash nor a backslash.
  if (!/^\/(?![/\\])/.test(returnTo)) return undefined
  let url: URL
  try {
    url = new URL(returnTo, "http://identity.invalid")
  } catch {
    return undefined
  }
  if (url.pathname !== AUTHORIZE_PATH) return undefined
  return validClientId(url.searchParams.get("client_id"))
}

/** The route this project publishes to Stytch as its Authorization URL. */
export const AUTHORIZE_ROUTE_NAME = "identity-authorize"

/**
 * The client_id gate, shared by both ways of reaching it.
 *
 * Absent is ordinary — a plain browser login has no client_id. Present but
 * unusable is an anomaly worth a line in the console, because its symptom is
 * expensive: the exchange loses the agent stamp AND the person is waitlisted,
 * which is the bug this whole area exists to fix, reproduced silently.
 *
 * That symptom stopped being hypothetical on 2026-09-18. Enabling CIMD made
 * Claude send a URL as its client_id, this gate refused it, the agent stamp
 * was lost, and every `network` person's Crucible sign-in landed on the
 * waitlist instead of the consent screen. Staff were unaffected and so it
 * looked fine: they are never waitlisted, they just lost the stamp silently.
 *
 * The forms it accepts are @auger/identity's, the same gate the exchange runs.
 */
export function validClientId(value: unknown): string | undefined {
  const raw = Array.isArray(value) ? value[0] : value
  if (typeof raw !== "string" || raw.length === 0) return undefined
  const clientId = sharedValidClientId(raw)
  if (!clientId)
    console.warn("Heavybit Identity: authorize return_to carried an unusable client_id")
  return clientId
}
